import React,{useCallback,useEffect,useMemo,useState} from 'react';
import {motion} from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import {
  fetchAdminPickupProblemReports,
  updateAdminPickupProblemStatus
} from '../../services/adminPickupProblemService';

const {
  FiAlertTriangle,
  FiRefreshCw,
  FiLoader,
  FiCheckCircle,
  FiClock,
  FiSearch
}=FiIcons;

const statusStyles={
  open:'bg-red-50 text-red-600',
  investigating:'bg-amber-50 text-amber-700',
  resolved:'bg-emerald-50 text-emerald-700'
};

const AdminPickupProblemsPanel=()=>{
  const [reports,setReports]=useState([]);
  const [status,setStatus]=useState('all');
  const [problemType,setProblemType]=useState('all');
  const [search,setSearch]=useState('');
  const [loading,setLoading]=useState(true);
  const [updatingId,setUpdatingId]=useState(null);
  const [error,setError]=useState('');

  const loadReports=useCallback(async()=>{
    setLoading(true);
    setError('');

    try {
      const data=await fetchAdminPickupProblemReports({
        status:status==='all' ? null : status,
        problemType:problemType==='all' ? null : problemType
      });
      setReports(data);
    } catch (requestError) {
      setError(requestError.message || 'Unable to load pickup problems.');
    } finally {
      setLoading(false);
    }
  },[problemType,status]);

  useEffect(()=>{
    loadReports();
  },[loadReports]);

  const filteredReports=useMemo(()=>{
    const query=search.trim().toLowerCase();

    if (!query) return reports;

    return reports.filter((report)=>[
      report.reporter_name,
      report.reporter_email,
      report.buyer_name,
      report.seller_business_name,
      report.problem_type,
      report.description,
      report.order_id
    ].some((value)=>String(value || '').toLowerCase().includes(query)));
  },[reports,search]);

  const handleStatusChange=async(reportId,nextStatus)=>{
    setUpdatingId(reportId);
    setError('');

    try {
      await updateAdminPickupProblemStatus(reportId,nextStatus);
      await loadReports();
    } catch (requestError) {
      setError(requestError.message || 'Unable to update this report.');
    } finally {
      setUpdatingId(null);
    }
  };

  const openCount=reports.filter((report)=>report.report_status==='open').length;
  const investigatingCount=reports.filter((report)=>report.report_status==='investigating').length;
  const resolvedCount=reports.filter((report)=>report.report_status==='resolved').length;

  return (
    <motion.section
      initial={{opacity:0,y:10}}
      animate={{opacity:1,y:0}}
      className="mt-8 space-y-6"
    >
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <SummaryCard icon={FiAlertTriangle} label="Open problems" value={openCount} tone="red" />
        <SummaryCard icon={FiClock} label="Investigating" value={investigatingCount} tone="amber" />
        <SummaryCard icon={FiCheckCircle} label="Resolved" value={resolvedCount} tone="emerald" />
      </div>

      <div className="bg-white rounded-[2.5rem] border border-gray-100 shadow-sm overflow-hidden">
        <div className="p-8 border-b border-gray-50 space-y-5">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
            <div>
              <h2 className="text-xl font-black text-gray-900">Pickup Problem Dashboard</h2>
              <p className="text-[10px] text-slate-400 font-black uppercase tracking-widest mt-1">
                Review and resolve reported pickup issues
              </p>
            </div>
            <button
              onClick={loadReports}
              className="flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-indigo-50 text-indigo-600 text-[10px] font-black uppercase tracking-widest"
            >
              <SafeIcon icon={FiRefreshCw} />
              Refresh
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="relative">
              <SafeIcon icon={FiSearch} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                value={search}
                onChange={(event)=>setSearch(event.target.value)}
                placeholder="Search reports..."
                className="w-full pl-11 pr-4 py-3 bg-gray-50 rounded-xl text-sm font-bold border-none outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
            <select
              value={status}
              onChange={(event)=>setStatus(event.target.value)}
              className="px-4 py-3 bg-gray-50 rounded-xl text-[10px] font-black uppercase tracking-widest border-none outline-none"
            >
              <option value="all">All statuses</option>
              <option value="open">Open</option>
              <option value="investigating">Investigating</option>
              <option value="resolved">Resolved</option>
            </select>
            <select
              value={problemType}
              onChange={(event)=>setProblemType(event.target.value)}
              className="px-4 py-3 bg-gray-50 rounded-xl text-[10px] font-black uppercase tracking-widest border-none outline-none"
            >
              <option value="all">All problem types</option>
              <option value="buyer_claims_not_received">Buyer did not receive</option>
              <option value="wrong_person">Wrong person</option>
              <option value="missing_items">Missing items</option>
              <option value="damaged_items">Damaged items</option>
              <option value="unable_to_verify">Unable to verify</option>
              <option value="expired_token">Expired token</option>
              <option value="connectivity_problem">Connectivity problem</option>
              <option value="other">Other</option>
            </select>
          </div>
        </div>

        {error && (
          <div className="mx-8 mt-6 p-4 rounded-2xl bg-red-50 text-red-700 text-sm font-bold">
            {error}
          </div>
        )}

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50/50 text-[10px] text-gray-400 uppercase tracking-widest">
                <th className="px-8 py-4 text-left">Report</th>
                <th className="px-8 py-4 text-left">Order / Parties</th>
                <th className="px-8 py-4 text-left">Description</th>
                <th className="px-8 py-4 text-left">Status</th>
                <th className="px-8 py-4 text-right">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {loading ? (
                <tr><td colSpan="5" className="py-20 text-center"><FiLoader className="animate-spin inline text-indigo-300 text-3xl" /></td></tr>
              ) : filteredReports.length===0 ? (
                <tr><td colSpan="5" className="py-20 text-center text-gray-400 font-bold uppercase text-[10px] tracking-widest">No pickup problems found</td></tr>
              ) : filteredReports.map((report)=>(
                <tr key={report.report_id} className="align-top">
                  <td className="px-8 py-5">
                    <p className="text-sm font-black text-gray-900">{report.problem_type.replaceAll('_',' ')}</p>
                    <p className="text-[9px] text-slate-400 font-bold uppercase mt-1">{new Date(report.created_at).toLocaleString()}</p>
                  </td>
                  <td className="px-8 py-5">
                    <p className="text-xs font-black text-indigo-600">#{report.order_id?.slice(0,8).toUpperCase()}</p>
                    <p className="text-xs font-bold text-gray-900 mt-2">{report.seller_business_name}</p>
                    <p className="text-[10px] text-slate-400">Buyer: {report.buyer_name}</p>
                  </td>
                  <td className="px-8 py-5 max-w-sm">
                    <p className="text-sm text-slate-600 whitespace-pre-wrap">{report.description || 'No description provided.'}</p>
                    <p className="text-[10px] text-slate-400 mt-2">Reported by {report.reporter_name}</p>
                  </td>
                  <td className="px-8 py-5">
                    <span className={`px-3 py-1 rounded-full text-[9px] font-black uppercase tracking-widest ${statusStyles[report.report_status] || 'bg-gray-50 text-gray-600'}`}>
                      {report.report_status}
                    </span>
                  </td>
                  <td className="px-8 py-5 text-right">
                    <select
                      value={report.report_status}
                      disabled={updatingId===report.report_id}
                      onChange={(event)=>handleStatusChange(report.report_id,event.target.value)}
                      className="px-3 py-2 bg-gray-50 rounded-xl text-[9px] font-black uppercase tracking-widest border-none disabled:opacity-50"
                    >
                      <option value="open">Open</option>
                      <option value="investigating">Investigating</option>
                      <option value="resolved">Resolved</option>
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </motion.section>
  );
};

const SummaryCard=({icon,label,value,tone})=>(
  <div className="bg-white rounded-3xl p-6 border border-gray-100 shadow-sm flex items-center gap-4">
    <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
      tone==='red' ? 'bg-red-50 text-red-600' :
      tone==='amber' ? 'bg-amber-50 text-amber-600' :
      'bg-emerald-50 text-emerald-600'
    }`}>
      <SafeIcon icon={icon} />
    </div>
    <div>
      <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">{label}</p>
      <p className="text-2xl font-black text-gray-900">{value}</p>
    </div>
  </div>
);

export default AdminPickupProblemsPanel;